/**
 * Dates métier `AAAA-MM-JJ` (calendrier, sans fuseau) et utilitaires en Europe/Paris (CDC §0).
 * Les calculs de calendrier se font en UTC pour être indépendants du fuseau de la machine.
 */

export const PARIS_TIME_ZONE = 'Europe/Paris';

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

const WEEKDAYS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'] as const;
const MONTHS_FR = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;
const MONTHS_SHORT_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

export interface DateParts {
  year: number;
  month: number; // 1 à 12
  day: number;
}

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE_RE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

export function parseIsoDate(value: string): DateParts {
  if (!isIsoDate(value)) throw new RangeError(`Date invalide (AAAA-MM-JJ attendu) : ${value}`);
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

export function formatIsoDate({ year, month, day }: DateParts): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

function toUtcMs(value: string): number {
  const { year, month, day } = parseIsoDate(value);
  return Date.UTC(year, month - 1, day);
}

function fromUtcMs(ms: number): string {
  const d = new Date(ms);
  return formatIsoDate({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() });
}

/** Date calendaire à Paris d'un instant (par défaut : maintenant). */
export function toParisDate(instant: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return formatIsoDate({ year: get('year'), month: get('month'), day: get('day') });
}

/** « Aujourd'hui » à Paris. */
export function todayInParis(now: Date = new Date()): string {
  return toParisDate(now);
}

/** Instant ISO (UTC) d'une date et d'une heure murales à Paris. */
export function parisDateTimeToIso(date: string, time: string): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const guess = new Date(`${date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00Z`);
  // Décalage de Paris à cet instant (UTC+1 ou UTC+2).
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(guess);
  const parisHours = Number(parts.find((p) => p.type === 'hour')?.value ?? hours);
  const offsetHours = (parisHours - guess.getUTCHours() + 24) % 24;
  return new Date(guess.getTime() - offsetHours * 3_600_000).toISOString();
}

export function addDays(value: string, days: number): string {
  return fromUtcMs(toUtcMs(value) + days * DAY_MS);
}

/** Nombre de jours de `from` à `to` (positif si `to` est après `from`). */
export function diffDays(from: string, to: string): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);
}

/** Jour de la semaine ISO : 1 = lundi … 7 = dimanche. */
export function isoWeekday(value: string): number {
  const day = new Date(toUtcMs(value)).getUTCDay();
  return day === 0 ? 7 : day;
}

export function isWeekend(value: string): boolean {
  return isoWeekday(value) >= 6;
}

/** Lundi de la semaine de la date. */
export function mondayOf(value: string): string {
  return addDays(value, 1 - isoWeekday(value));
}

/** Ajoute (ou retire) des jours ouvrés (lundi à vendredi). */
export function addBusinessDays(value: string, days: number): string {
  let current = value;
  let remaining = Math.abs(days);
  const step = days < 0 ? -1 : 1;
  while (remaining > 0) {
    current = addDays(current, step);
    if (!isWeekend(current)) remaining -= 1;
  }
  return current;
}

/** « lundi 12 octobre 2026 » */
export function formatLongFr(value: string): string {
  const { year, month, day } = parseIsoDate(value);
  const weekday = WEEKDAYS_FR[new Date(toUtcMs(value)).getUTCDay()];
  return `${weekday} ${day === 1 ? '1er' : day} ${MONTHS_FR[month - 1]} ${year}`;
}

/** « 12 oct. » */
export function formatShortFr(value: string): string {
  const { month, day } = parseIsoDate(value);
  return `${day === 1 ? '1er' : day} ${MONTHS_SHORT_FR[month - 1]}`;
}
